import {
  Alert,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
} from '@mui/material';
import type { ReactElement } from 'react';
import { useGetList } from 'react-admin';

import { useBabyScope } from '@/resources/babyScope';

/**
 * Choix du profil bebe, **local a l'ecran qui en depend**.
 *
 * Ce selecteur vivait dans la barre superieure, sur toutes les pages. C'etait le
 * coeur du reproche du PO : un outil d'administration qui affiche en permanence
 * « Profil bebe : … » se lit comme le carnet de suivi d'un enfant, quoi qu'il
 * montre en dessous. Il est desormais rendu **par les seuls ecrans dont la route
 * d'API porte `{babyId}`**, c'est-a-dire sept ressources sur neuf ; les deux
 * ressources globales n'affichent plus rien qui parle de portee.
 *
 * Ce qui n'a pas change, et ne doit pas changer : l'ecriture passe par
 * `setBabyId`, **seul point qui tient synchrones** le store React Admin et la
 * reference hors React lue par le `dataProvider` (`currentBabyIdRef`). Ce module
 * n'ecrit jamais l'une des deux representations directement.
 *
 * La liste vient de la ressource `babies`, donc du serveur : elle est filtree
 * sur l'appartenance, et aucun identifiant n'est saisi a la main.
 */

interface BabyRecord {
  readonly id: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly archived?: boolean;
}

/** Un profil se nomme par son prenom, jamais par son identifiant technique. */
export const babyLabel = (baby: BabyRecord): string => {
  const name = [baby.firstName, baby.lastName]
    .filter((part): part is string => part !== undefined && part !== '')
    .join(' ');
  const base = name === '' ? 'Profil sans nom' : name;

  return baby.archived === true ? `${base} (archivé)` : base;
};

export interface BabyScopeSelectorProps {
  /**
   * Phrase affichee sous le champ. Elle differe selon l'ecran : sur une liste
   * deja filtree, elle rappelle ce que le filtre fait ; sur un ecran vide, elle
   * dit ce qui manque.
   */
  readonly helperText?: string;
  readonly autoFocus?: boolean;
}

export const BabyScopeSelector = ({
  helperText,
  autoFocus,
}: BabyScopeSelectorProps): ReactElement => {
  const { babyId, setBabyId } = useBabyScope();
  const { data, isPending, error } = useGetList<BabyRecord & { id: string }>('babies', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'firstName', order: 'ASC' },
  });

  if (error !== null) {
    return (
      <Alert severity="error" variant="outlined">
        La liste des profils n’a pas pu être chargée. Vérifiez que l’API est joignable, puis
        rechargez la page.
      </Alert>
    );
  }

  if (isPending) {
    return <Skeleton variant="rounded" height={56} sx={{ maxWidth: 320 }} />;
  }

  const babies = data;
  const known = babies.some((baby) => baby.id === babyId);

  return (
    <FormControl size="small" sx={{ minWidth: 260 }}>
      {/* `shrink` et `notched` : avec `displayEmpty`, le texte « Choisissez un
          profil » occupe le champ, et un libelle non remonte s'y superposait. */}
      <InputLabel id="baby-scope-label" shrink>
        Profil bébé
      </InputLabel>
      <Select
        labelId="baby-scope-label"
        label="Profil bébé"
        notched
        autoFocus={autoFocus === true}
        // Une valeur inconnue de la liste — profil retire, session changee —
        // remettrait le champ en etat « hors controle » de Material UI : on
        // retombe sur le placeholder plutot que d'afficher un identifiant.
        value={known ? babyId : ''}
        displayEmpty
        renderValue={(value) => {
          const match = babies.find((baby) => baby.id === value);

          return match === undefined ? 'Choisissez un profil' : babyLabel(match);
        }}
        onChange={(event) => {
          const next = event.target.value;

          setBabyId(next === '' ? null : next);
        }}
      >
        {babies.length === 0 ? (
          <MenuItem value="" disabled>
            Aucun profil accessible avec ce compte
          </MenuItem>
        ) : null}
        {babies.map((baby) => (
          <MenuItem key={baby.id} value={baby.id}>
            {babyLabel(baby)}
          </MenuItem>
        ))}
      </Select>
      {helperText === undefined ? null : <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

/** Bandeau de filtre pose au-dessus d'une liste portee par un profil. */
export const BabyScopeBar = (): ReactElement => (
  <Box sx={{ mb: 2 }}>
    <BabyScopeSelector helperText="Les lignes ci-dessous sont celles de ce profil." />
  </Box>
);
